"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Bell } from "lucide-react";
import { COVERAGE } from "@/lib/marketing/copy";
import { SectionHeader } from "@/components/common/SectionHeader";
import { GlassCard } from "@/components/common/GlassCard";

interface Area {
  id: string;
  name: string;
  city: string;
  isActive: boolean;
}

export function CoverageSection() {
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAreas() {
      try {
        const response = await fetch("/api/public/areas");
        if (response.ok) {
          const data = await response.json();
          setAreas(data.filter((a: Area) => a.isActive));
        }
      } catch (error) {
        console.error("Failed to fetch areas:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAreas();
  }, []);

  const groupedByCity = areas.reduce((acc, area) => {
    if (!acc[area.city]) {
      acc[area.city] = [];
    }
    acc[area.city].push(area);
    return acc;
  }, {} as Record<string, Area[]>);

  return (
    <section id="coverage" className="zaklyn-section bg-secondary/30">
      <div className="zaklyn-container">
        <SectionHeader
          title={COVERAGE.title}
          subtitle={COVERAGE.subtitle}
        />

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-12 bg-secondary rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByCity).map(([city, cityAreas], cityIndex) => (
              <motion.div
                key={city}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: cityIndex * 0.1 }}
              >
                <h3 className="font-display text-xl font-semibold text-foreground mb-4">
                  {city}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {cityAreas.map((area, index) => (
                    <motion.div
                      key={area.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.03 }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-border/50 text-sm font-body text-foreground"
                    >
                      <MapPin className="h-3.5 w-3.5 text-primary" />
                      {area.name}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <GlassCard hover={false} className="inline-block p-6 md:p-8">
            <p className="text-muted-foreground font-body mb-4">
              {COVERAGE.expandingText}
            </p>
            <button className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-foreground font-medium rounded-xl hover:bg-secondary/80 transition-colors">
              <Bell className="h-4 w-4" />
              {COVERAGE.notifyMe}
            </button>
          </GlassCard>
        </motion.div>
      </div>
    </section>
  );
}
